import { z } from "zod";
import { zId, zIsoDate, zMoney } from "./common.js";

/**
 * Planned / deferred giving (CT-RM-18). These are commitments, not cash: they never enter
 * gift totals. Open intentions + documented commitments form the planned-giving pipeline;
 * a matured one links to the real gift that was received.
 */
export const PLANNED_GIFT_VEHICLES = ["bequest", "charitable_gift_annuity", "trust", "beneficiary_designation", "other"] as const;
export const zPlannedGiftVehicle = z.enum(PLANNED_GIFT_VEHICLES);
export type PlannedGiftVehicle = z.infer<typeof zPlannedGiftVehicle>;

export const PLANNED_GIFT_VEHICLE_LABELS: Record<PlannedGiftVehicle, string> = {
  bequest: "Bequest",
  charitable_gift_annuity: "Charitable gift annuity",
  trust: "Trust",
  beneficiary_designation: "Beneficiary designation",
  other: "Other",
};

export const PLANNED_GIFT_STATUSES = ["intention", "documented", "realized", "revoked"] as const;
export const zPlannedGiftStatus = z.enum(PLANNED_GIFT_STATUSES);
export type PlannedGiftStatus = z.infer<typeof zPlannedGiftStatus>;

export const PLANNED_GIFT_STATUS_LABELS: Record<PlannedGiftStatus, string> = {
  intention: "Intention",
  documented: "Confirmed / documented",
  realized: "Matured / realized",
  revoked: "Revoked",
};

/** statuses that count toward the pipeline (expected but not yet received) */
export const PLANNED_PIPELINE_STATUSES: readonly PlannedGiftStatus[] = ["intention", "documented"];

export const zPlannedGift = z.object({
  id: zId,
  constituentId: zId,
  constituentName: z.string(),
  vehicle: zPlannedGiftVehicle,
  status: zPlannedGiftStatus,
  estimatedValue: zMoney.nullable(),
  expectedMaturityDate: zIsoDate.nullable(),
  fundId: zId.nullable(),
  fundName: z.string().nullable(),
  realizedGiftId: zId.nullable(),
  /** the linked gift's actual amount (cash), shown beside the estimate */
  realizedAmount: zMoney.nullable(),
  realizedAt: zIsoDate.nullable(),
  documents: z.array(z.object({ id: zId, fileName: z.string() })),
  notes: z.string().nullable(),
  legacySocietyJoinedOn: zIsoDate.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlannedGift = z.infer<typeof zPlannedGift>;

const zNullableMoney = zMoney.nullable().optional();

export const zCreatePlannedGift = z.object({
  constituentId: zId,
  vehicle: zPlannedGiftVehicle,
  status: zPlannedGiftStatus.default("intention"),
  estimatedValue: zNullableMoney,
  expectedMaturityDate: zIsoDate.nullable().optional(),
  fundId: zId.nullable().optional(),
  /** required when status = realized: the gift that was received */
  realizedGiftId: zId.nullable().optional(),
  realizedAt: zIsoDate.nullable().optional(),
  /** ids from the constituent's Files (existing document storage) */
  documentIds: z.array(zId).max(20).default([]),
  notes: z.string().max(5000).nullable().optional(),
});
export type CreatePlannedGift = z.infer<typeof zCreatePlannedGift>;

export const zUpdatePlannedGift = zCreatePlannedGift
  .omit({ constituentId: true })
  .partial()
  .extend({ documentIds: z.array(zId).max(20).optional() })
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type UpdatePlannedGift = z.infer<typeof zUpdatePlannedGift>;

export const zPlannedGiftListQuery = z.object({
  status: zPlannedGiftStatus.optional(),
  vehicle: zPlannedGiftVehicle.optional(),
  constituentId: zId.optional(),
  legacySociety: z.enum(["true", "false"]).optional(),
});
export type PlannedGiftListQuery = z.infer<typeof zPlannedGiftListQuery>;

export const zPlannedGiftSummary = z.object({
  /** intention + documented: expected, not received */
  pipeline: z.object({ count: z.number().int(), estimatedValue: zMoney }),
  byStatus: z.record(zPlannedGiftStatus, z.object({ count: z.number().int(), estimatedValue: zMoney })),
  /** matured gifts: their estimates and the cash actually received through the linked gifts */
  realized: z.object({ count: z.number().int(), estimatedValue: zMoney, receivedAmount: zMoney }),
  legacySocietyMembers: z.number().int(),
});
export type PlannedGiftSummary = z.infer<typeof zPlannedGiftSummary>;

/** PUT /constituents/:id/legacy-society — null removes the membership */
export const zSetLegacySociety = z.object({ joinedOn: zIsoDate.nullable() });
export type SetLegacySociety = z.infer<typeof zSetLegacySociety>;

/** Why a planned gift can't take this status/link, or null (API guard + form hint). */
export function plannedGiftProblem(v: { status?: PlannedGiftStatus; realizedGiftId?: string | null }): string | null {
  if (v.status === "realized" && !v.realizedGiftId) return "Link the gift that was received to mark this planned gift realized.";
  if (v.status && v.status !== "realized" && v.realizedGiftId) return "Only a realized planned gift links to a received gift.";
  return null;
}
